import React from 'react';
import { Spinner } from '../../shared';
import { FlagHistoryEntry, UserGamification } from '../api/types';
import { ActivitySparkline } from './ActivitySparkline';

/** The heuristics gamification-fraud-flagger records, with human labels. */
const SIGNAL_LABELS: Record<string, string> = {
  impossible_travel: 'Impossible travel',
  device_dup: 'Duplicate device',
  referral_farm: 'Referral farming',
};

const ACTION_LABELS: Record<string, string> = {
  user_auto_flagged: 'Auto-flagged',
  user_flagged: 'Flagged by admin',
  user_unflagged: 'Unflagged by admin',
};

interface SignalSummary {
  signal: string;
  hits: number;
  lastSeen: string;
}

/**
 * Roll the system-actor rows up per heuristic: how many times it fired and when
 * it last did. History is newest-first, so the first hit seen is the latest.
 */
function summarizeSignals(history: FlagHistoryEntry[]): SignalSummary[] {
  const bySignal = new Map<string, SignalSummary>();
  for (const entry of history) {
    if (entry.actor_role !== 'system' || !entry.signal) continue;
    const existing = bySignal.get(entry.signal);
    if (existing) {
      existing.hits += 1;
    } else {
      bySignal.set(entry.signal, { signal: entry.signal, hits: 1, lastSeen: entry.occurred_at });
    }
  }
  return [...bySignal.values()].sort((a, b) => b.hits - a.hits);
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * Fraud detail for one user: which heuristics fired, the flag/unflag timeline,
 * and the last-7-day coin activity. Renders explicit empty states — a clean user
 * is a normal result, not a missing one.
 */
export function FraudDetail({ user, loading }: { user: UserGamification; loading: boolean }) {
  const history = user.flag_history;
  const activity = user.activity;

  if (loading && !history) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="sm" />
      </div>
    );
  }

  const signals = summarizeSignals(history ?? []);

  return (
    <div className="mt-4 pt-4 border-t border-gray-800 space-y-4">
      {/* Fraud signals */}
      <div>
        <p className="text-xs font-medium text-gray-400 mb-2">Fraud signals</p>
        {signals.length === 0 ? (
          <p className="text-xs text-gray-500">No heuristic has fired for this user.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {signals.map((s) => (
              <span
                key={s.signal}
                title={`Last fired ${formatWhen(s.lastSeen)}`}
                className="px-2 py-1 text-xs rounded-md bg-amber-950/50 border border-amber-800/50 text-amber-300"
              >
                {SIGNAL_LABELS[s.signal] ?? s.signal}
                {s.hits > 1 && <span className="text-amber-500/80"> ×{s.hits}</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Flag history */}
      <div>
        <p className="text-xs font-medium text-gray-400 mb-2">Flag history</p>
        {!history || history.length === 0 ? (
          <p className="text-xs text-gray-500">Never flagged.</p>
        ) : (
          <ol className="space-y-2">
            {history.map((entry, i) => (
              <li key={`${entry.occurred_at}-${i}`} className="flex gap-2 text-xs">
                <span
                  className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                    entry.action === 'user_unflagged' ? 'bg-emerald-400' : 'bg-red-400'
                  }`}
                />
                <div className="min-w-0">
                  <p className="text-gray-300">
                    {ACTION_LABELS[entry.action] ?? entry.action}
                    {entry.signal && (
                      <span className="text-gray-500">
                        {' — '}
                        {SIGNAL_LABELS[entry.signal] ?? entry.signal}
                      </span>
                    )}
                  </p>
                  <p className="text-gray-600">
                    {formatWhen(entry.occurred_at)}
                    {entry.actor_email ? ` · ${entry.actor_email}` : ` · ${entry.actor_role}`}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Activity sparkline */}
      <div>
        <p className="text-xs font-medium text-gray-400 mb-2">
          Coin activity · last {activity?.days ?? 7} days
        </p>
        {activity ? (
          <ActivitySparkline series={activity.series} />
        ) : (
          <p className="text-xs text-gray-500">No activity data.</p>
        )}
      </div>
    </div>
  );
}
